import React from 'react';

export interface ConnectionEndpoint {
    /** Icon/glyph shown in the tile (coloured white when a `background` is set). */
    icon: React.ReactNode;
    /** Optional name — shown as a caption under the tiles and used as the tile's label. */
    label?: string;
    /** Tile background. When omitted, a neutral surface is used. */
    background?: string;
}

export interface ConnectionHeaderProps {
    from: ConnectionEndpoint;
    to: ConnectionEndpoint;
    className?: string;
}

const tileStyle = (endpoint: ConnectionEndpoint): React.CSSProperties => ({
    backgroundColor: endpoint.background ?? 'var(--malachite-card-dimmed-background)',
    color: endpoint.background ? '#fff' : 'var(--malachite-text-primary)',
});

const ConnectionHeader: React.FC<ConnectionHeaderProps> = ({ from, to, className = '' }) => {
    const hasLabels = Boolean(from.label || to.label);

    return (
        <div className={`flex flex-col items-center gap-[12px] ${className}`}>
            <div className="flex items-center justify-center">
                <div
                    // 13px is a ~0.325 ratio of the 40px tile, not a rung on the radius
                    // scale, so it stays a literal (Avatar does the same with 0.25).
                    className="flex items-center justify-center size-[40px] rounded-[13px] mr-[-8px] z-[2]"
                    style={{
                        ...tileStyle(from),
                        transform: 'rotate(-12deg)',
                        boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.15)',
                    }}
                    aria-label={from.label}
                >
                    {from.icon}
                </div>
                <div
                    className="flex items-center justify-center size-[40px] rounded-[13px] border-[1.5px] border-solid border-[var(--malachite-card-background)] z-[1]"
                    style={{
                        ...tileStyle(to),
                        transform: 'rotate(12deg)',
                        boxShadow: '0px 4px 4px 0px rgba(0,0,0,0.15)',
                    }}
                    aria-label={to.label}
                >
                    {to.icon}
                </div>
            </div>
            {hasLabels && (
                <div className="flex items-center gap-[8px] text-[13px] font-medium text-[var(--malachite-text-secondary)]">
                    {from.label && <span>{from.label}</span>}
                    {from.label && to.label && (
                        <span className="text-[var(--malachite-text-placeholder)]">→</span>
                    )}
                    {to.label && <span>{to.label}</span>}
                </div>
            )}
        </div>
    );
};

export default ConnectionHeader;
export { ConnectionHeader };
